import { localizeRuntimeById } from "@app/runtime-localize";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { FgdsProductType } from "@domain/product/product";
import { AlarmDeviceConfiguration } from "@domain/project/configurations/alarm-device-configuration";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { GasWarningCenterConfiguration } from "@domain/project/configurations/gas-warning-center-configuration";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { PlasticSignConfiguration } from "@domain/project/configurations/plastic-sign-configuration";
import { SignalElementConfiguration } from "@domain/project/configurations/signal-element-configuration";
import { TransmitterConfiguration } from "@domain/project/configurations/transmitter-configuration";
import { FgdsProject } from "@domain/project/fgds-project";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { MonitoringProductData } from "@domain/project/monitoring-product-data/monitoring-product-data";
import { MonitoringCategorySection } from "@domain/project/monitoring-product-list/monitoring-category-section";
import { MonitoringConfigProductMap } from "@domain/project/monitoring-product-list/monitoring-config-product-map";
import { MonitoringGroupSection } from "@domain/project/monitoring-product-list/monitoring-group-section";
import { AdditionalMonitoringConfigSection } from "@domain/project/monitoring-product-list/monitoring-product-list.service";
import { ProductData } from "@domain/project/product-data/product-data";
import {
  AdditionalProductsTableItem,
  getAdditionalProductDraftQuantity,
  groupVisibleAdditionalProductSections,
  sortAdditionalProductsByPositionId,
} from "@domain/project/product-list/additional-products-display.util";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { GroupSection } from "@domain/project/product-list/group-section";
import { calculateDiscountedPrice } from "@domain/project/product-list/product-list-discount.util";
import { AdditionalProductConfigSection } from "@domain/project/product-list/product-list.service";
import { ProductSection } from "@domain/project/product-list/product-section";

export interface ProductListProductSectionDisplayData<SectionData> {
  key: string;
  label: string;
  subtotal?: number;
  section: SectionData;
}

export interface ProductListAttachmentInstructionsDisplayData {
  key: string;
  label: string;
  subtotal: number;
  attachmentInstructions: ProductData[];
}

export interface ProductListGroupDisplayData<SectionData, ExtraSectionData, SourceData> {
  key: string;
  label: string;
  subtotal?: number;
  source: SourceData;
  sections: Array<ProductListProductSectionDisplayData<SectionData> | ExtraSectionData>;
}

export interface AdditionalConfigDisplayData<SubproductData extends AdditionalProductsTableItem> {
  configId: string;
  positionNumber?: string;
  name: string;
  mainProductId?: string;
  mainProduct?: ProductData;
  savedQuantity: number;
  unitPrice?: number;
  total?: number;
  zrefUnitPrice?: number;
  zrefTotal?: number;
  showWarning?: boolean;
  isAvailable?: boolean;
  isIncompleteConfiguration?: boolean;
  subproducts: SubproductData[];
}

export interface AdditionalCategoryDisplayData<SubproductData extends AdditionalProductsTableItem> {
  key: string;
  label: string;
  subtotal?: number;
  zrefSubTotal?: number;
  configs: AdditionalConfigDisplayData<SubproductData>[];
}

export type FgdsSectionDisplayData = ProductSection<
  | FloorplanAlarmDevice
  | FloorplanGasWarningCenter
  | FloorplanPlasticSign
  | FloorplanSignalElement
  | FloorplanTransmitter
  | FloorplanFlameDetector,
  FloorplanPlaceholder,
  | AlarmDeviceConfiguration
  | GasWarningCenterConfiguration
  | PlasticSignConfiguration
  | SignalElementConfiguration
  | TransmitterConfiguration
  | FlameDetectorConfiguration,
  ConfigProductMap
>;
export type MonitoringSectionDisplayData = MonitoringCategorySection<
  FloorplanPatientMonitor | FloorplanCentralStation | FloorplanTelemetry | FloorplanKvm | FloorplanDisplay | FloorplanUtility,
  FloorplanSimplePlaceholder,
  | PatientMonitorConfiguration
  | CentralStationConfiguration
  | TelemetryConfiguration
  | KvmConfiguration
  | DisplayConfiguration
  | UtilityConfiguration,
  MonitoringConfigProductMap
>;

interface FgdsSectionDescriptor {
  key: string;
  label?: string;
  translationKey: string;
  fallback: string;
  getSection: (group: GroupSection) => FgdsSectionDisplayData;
}

interface MonitoringSectionDescriptor {
  key: string;
  label?: string;
  translationKey: string;
  fallback: string;
  getSection: (group: MonitoringGroupSection) => MonitoringSectionDisplayData;
}

const FGDS_PLACED_SECTION_DESCRIPTORS: FgdsSectionDescriptor[] = [
  {
    key: "gasWarningCenters",
    label: $localize`:@@global.gasWarningCenters:Gaswarnzentralen`,
    translationKey: "global.gasWarningCenters",
    fallback: "Gaswarnzentralen",
    getSection: (group) => group.gasWarningCenters,
  },
  {
    key: "transmitters",
    translationKey: "global.transmitters",
    fallback: "Transmitter",
    getSection: (group) => group.transmitters,
  },
  {
    key: "alarmDevices",
    translationKey: "global.alarmDevices",
    fallback: "Alarmmittel",
    getSection: (group) => group.alarmDevices,
  },
  {
    key: "signalElements",
    translationKey: "global.signalElements",
    fallback: "Leuchttransparente",
    getSection: (group) => group.signalElements,
  },
  {
    key: "plasticSigns",
    translationKey: "global.plasticSigns",
    fallback: "Kunststoffschilder",
    getSection: (group) => group.plasticSigns,
  },
  {
    key: "flameDetectors",
    translationKey: "global.flameDetectors",
    fallback: "Brandmelder",
    getSection: (group) => group.flameDetectors,
  },
];

const MONITORING_PLACED_SECTION_DESCRIPTORS: MonitoringSectionDescriptor[] = [
  {
    key: "patientMonitors",
    translationKey: "monitoring.patientMonitors",
    fallback: "Patienten Monitore",
    getSection: (group) => group.patientMonitors,
  },
  {
    key: "centralStations",
    translationKey: "monitoring.centralStations",
    fallback: "Central Stations",
    getSection: (group) => group.centralStations,
  },
  {
    key: "telemetries",
    translationKey: "monitoring.telemetries",
    fallback: "Telemetries",
    getSection: (group) => group.telemetries,
  },
  {
    key: "kvms",
    translationKey: "monitoring.kvms",
    fallback: "KVMs",
    getSection: (group) => group.kvms,
  },
  {
    key: "displays",
    translationKey: "monitoring.displays",
    fallback: "Displays",
    getSection: (group) => group.displays,
  },
  {
    key: "utilities",
    translationKey: "monitoring.utilities",
    fallback: "Utilities",
    getSection: (group) => group.utilities,
  },
];

export const DIRECT_BOM_CATEGORY_KEY = "DIRECT_BOM";
export const ZREF_EUR_COUNTRY = "DE";
export const additionalProductsLabel = $localize`:@@productList.copyToClipboard.additionalProducts:Zusätzliche Produkte`;
export const attachmentInstructionsLabel = $localize`:@@productList.copyToClipboard.attachmentInstructions:Gebrauchsanweisungen für Zubehör`;
export const servicesLabel = $localize`:@@.productList.copyToClipboard.services:Services`;
export const productsOnFloorplanLabel = $localize`:@@productList.productsOnFloorPlan:Produkte auf dem Grundrissplan`;

function getDirectBomEntriesLabel(): string {
  return $localize`:@@productList.directBomEntries:Einzelne Produkte`;
}

function getFgdsAdditionalCategoryLabel(type: FgdsProductType): string {
  switch (type) {
    case FgdsProductType.ALARMDEVICE:
      return localizeRuntimeById("global.alarmDevices", "Alarmmittel");
    case FgdsProductType.GASWARNINGCENTER:
      return localizeRuntimeById("global.gasWarningCenters", "Gaswarnzentralen");
    case FgdsProductType.PLASTICSIGN:
      return localizeRuntimeById("global.plasticSigns", "Kunststoffschilder");
    case FgdsProductType.SIGNALELEMENT:
      return localizeRuntimeById("global.signalElements", "Leuchttransparente");
    case FgdsProductType.TRANSMITTER:
      return localizeRuntimeById("global.transmitters", "Transmitter");
    case FgdsProductType.ATTACHMENT:
      return localizeRuntimeById("productList.attachments", "Zubehör");
    case FgdsProductType.SENSOR:
      return localizeRuntimeById("productList.sensors", "Sensoren");
    case FgdsProductType.MISCDEVICE:
      return localizeRuntimeById("productList.miscDevices", "Sonstige Geräte");
    case FgdsProductType.SERVICE:
      return localizeRuntimeById("productList.services", "Dienstleistungen");
    case FgdsProductType.FLAME_DETECTOR:
      return localizeRuntimeById("global.flameDetectors", "Brandmelder");
    default:
      return "";
  }
}

function getMonitoringAdditionalCategoryLabel(category: MonitoringCategory): string {
  switch (category) {
    case MonitoringCategory.PATIENT_MONITORS:
      return localizeRuntimeById("monitoring.patientMonitors", "Patienten Monitore");
    case MonitoringCategory.CENTRAL_STATIONS:
      return localizeRuntimeById("monitoring.centralStations", "Central Stations");
    case MonitoringCategory.TELEMETRY:
      return localizeRuntimeById("monitoring.telemetries", "Telemetries");
    case MonitoringCategory.KVMS:
      return localizeRuntimeById("monitoring.kvms", "KVMs");
    case MonitoringCategory.DISPLAYS:
      return localizeRuntimeById("monitoring.displays", "Displays");
    case MonitoringCategory.UTILITY:
      return localizeRuntimeById("monitoring.utilities", "Utilities");
    default:
      return "";
  }
}

function hasVisibleSectionContent(section?: { mappings: unknown[]; devices: unknown[]; placeholders: unknown[] }) {
  if (!section) {
    return false;
  }

  return section.mappings.length > 0 || section.devices.length > 0 || section.placeholders.length > 0;
}

export function buildFgdsPlacedGroupDisplayData(
  groupSections: GroupSection[],
  getAttachmentInstructionsForGroup: (group: GroupSection) => ProductData[],
  getAttachmentInstructionsPrice: (instructions: ProductData[]) => number,
) {
  return groupSections
    .map<ProductListGroupDisplayData<FgdsSectionDisplayData, ProductListAttachmentInstructionsDisplayData, GroupSection>>(
      (group) => {
        const sections: Array<
          ProductListProductSectionDisplayData<FgdsSectionDisplayData> | ProductListAttachmentInstructionsDisplayData
        > = FGDS_PLACED_SECTION_DESCRIPTORS.map<ProductListProductSectionDisplayData<FgdsSectionDisplayData> | null>(
          (descriptor) => {
            const section = descriptor.getSection(group);
            return hasVisibleSectionContent(section)
              ? {
                  key: descriptor.key,
                  label: localizeRuntimeById(descriptor.translationKey, descriptor.fallback),
                  subtotal: section.subtotal,
                  section,
                }
              : null;
          },
        ).filter((section): section is ProductListProductSectionDisplayData<FgdsSectionDisplayData> => section !== null);

        const attachmentInstructions = getAttachmentInstructionsForGroup(group);
        if (attachmentInstructions.length > 0) {
          sections.push({
            key: "attachmentInstructions",
            label: localizeRuntimeById("productList.attachmentInstructions", "Gebrauchsanweisungen für Zubehör"),
            subtotal: getAttachmentInstructionsPrice(attachmentInstructions),
            attachmentInstructions,
          });
        }

        return {
          key: group.name,
          label: group.name,
          source: group,
          subtotal: group.subTotal,
          sections,
        };
      },
    )
    .filter((group) => group.sections.length > 0);
}

export function buildMonitoringPlacedGroupDisplayData(groupSections: MonitoringGroupSection[]) {
  return groupSections
    .map<ProductListGroupDisplayData<MonitoringSectionDisplayData, never, MonitoringGroupSection>>((group) => ({
      key: group.name,
      label: group.name,
      source: group,
      sections:
        MONITORING_PLACED_SECTION_DESCRIPTORS.map<ProductListProductSectionDisplayData<MonitoringSectionDisplayData> | null>(
          (descriptor) => {
            const section = descriptor.getSection(group);
            return hasVisibleSectionContent(section)
              ? {
                  key: descriptor.key,
                  label: localizeRuntimeById(descriptor.translationKey, descriptor.fallback),
                  section,
                }
              : null;
          },
        ).filter((section): section is ProductListProductSectionDisplayData<MonitoringSectionDisplayData> => section !== null),
    }))
    .filter((group) => group.sections.length > 0);
}

export function buildFgdsAdditionalCategoryDisplayData(
  editMode: boolean,
  sections: AdditionalProductConfigSection[],
  drafts: Record<string, number>,
) {
  return groupVisibleAdditionalProductSections(editMode, sections, drafts, (section) =>
    section.isDirectBomEntry ? DIRECT_BOM_CATEGORY_KEY : section.productSection.productType,
  ).map((group) => {
    const configs = group.sections.map((section) => {
      const savedQuantity = section.config.additionalProductListQuantity;
      const draftQuantity = getAdditionalProductDraftQuantity(editMode, sections, drafts, section.config.id);
      const mapping = section.productSection.mappings[0];
      const products: ProductData[] = mapping?.data ?? [];
      const hasStandardPrice = products.some((product) => product.priceData != null);
      const unitPrice = hasStandardPrice
        ? products.reduce((sum, product) => sum + (product.priceData?.price ?? 0) * product.quantity, 0)
        : savedQuantity > 0
          ? section.subtotal / savedQuantity
          : 0;
      const discountDelta = products.reduce((sum, product) => {
        const appliesToCategory =
          section.isDirectBomEntry ||
          product.type === section.productSection.productType ||
          product.type === FgdsProductType.ATTACHMENT;
        if (!product.priceData || !appliesToCategory) {
          return sum;
        }
        const rowQuantity =
          section.isDirectBomEntry ||
          product.type === FgdsProductType.ATTACHMENT ||
          product.type === FgdsProductType.GASWARNINGCENTER
            ? product.quantity
            : 1;
        return (
          sum + (calculateDiscountedPrice(product.priceData.price, product.discount ?? 0) - product.priceData.price) * rowQuantity
        );
      }, 0);
      const subTotal =
        section.isDirectBomEntry && hasStandardPrice
          ? products.reduce(
              (sum, product) =>
                sum +
                (product.priceData ? calculateDiscountedPrice(product.priceData.price, product.discount ?? 0) : 0) *
                  product.quantity,
              0,
            ) * draftQuantity
          : (savedQuantity > 0 ? section.subtotal / savedQuantity : 0) * draftQuantity + discountDelta * draftQuantity;
      const zrefSubTotal = products.some((p) => p.zrefPriceData != null)
        ? products.reduce(
            (sum, p) =>
              sum + (p.zrefPriceData ? calculateDiscountedPrice(p.zrefPriceData.price, p.discount ?? 0) : 0) * p.quantity,
            0,
          )
        : undefined;
      const zrefUnitPrice = products.some((p) => p.zrefPriceData != null)
        ? savedQuantity > 0
          ? products.reduce((sum, p) => sum + (p.zrefPriceData?.price ?? 0) * p.quantity, 0) / savedQuantity
          : undefined
        : undefined;

      return {
        configId: section.config.id,
        positionNumber: section.config.positionNumber,
        name: section.config.name,
        mainProductId: section.config.mainProductId,
        mainProduct: products[0],
        savedQuantity,
        unitPrice,
        total: savedQuantity > 0 ? subTotal : 0,
        zrefUnitPrice,
        zrefTotal: zrefSubTotal != null && savedQuantity > 0 ? (zrefSubTotal / savedQuantity) * draftQuantity : undefined,
        showWarning: (mapping?.data ?? []).some(
          (product) => product.isAvailable === false || product.isIncompleteConfiguration === true,
        ),
        isAvailable: mapping?.data[0]?.isAvailable,
        isIncompleteConfiguration: (mapping?.data ?? []).some((product) => product.isIncompleteConfiguration === true),
        subproducts: sortAdditionalProductsByPositionId(mapping?.data.slice(1) ?? []),
      };
    });

    return {
      key: group.key,
      label:
        group.key === DIRECT_BOM_CATEGORY_KEY
          ? getDirectBomEntriesLabel()
          : getFgdsAdditionalCategoryLabel(group.key as FgdsProductType),
      subtotal: configs.reduce((total, config) => total + (config.total ?? 0), 0),
      zrefSubTotal: configs.some((c) => c.zrefTotal != null)
        ? configs.reduce((total, config) => total + (config.zrefTotal ?? 0), 0)
        : undefined,
      configs,
    } satisfies AdditionalCategoryDisplayData<ProductData>;
  });
}

export function buildMonitoringAdditionalCategoryDisplayData(
  editMode: boolean,
  sections: AdditionalMonitoringConfigSection[],
  drafts: Record<string, number>,
) {
  return groupVisibleAdditionalProductSections(editMode, sections, drafts, (section) =>
    section.isDirectBomEntry ? DIRECT_BOM_CATEGORY_KEY : (section.productSection.category as MonitoringCategory),
  ).map((group) => ({
    key: group.key,
    label:
      group.key === DIRECT_BOM_CATEGORY_KEY
        ? getDirectBomEntriesLabel()
        : getMonitoringAdditionalCategoryLabel(group.key as MonitoringCategory),
    configs: group.sections.map((section) => {
      const mainProduct = section.productSection.mappings[0]?.data[0];

      return {
        configId: section.config.id,
        positionNumber: section.config.positionNumber,
        name: mainProduct?.name ?? section.config.name,
        mainProductId: mainProduct?.matNr ?? section.config.mainProductId,
        savedQuantity: section.config.additionalProductListQuantity,
        showWarning: (section.productSection.mappings[0]?.data ?? []).some((product) => product.isAvailable === false),
        isAvailable: section.productSection.mappings[0]?.data[0]?.isAvailable,
        subproducts: sortAdditionalProductsByPositionId(section.productSection.mappings[0]?.data.slice(1) ?? []),
      };
    }),
  })) satisfies AdditionalCategoryDisplayData<MonitoringProductData>[];
}

export function buildFgdsAdditionalAttachmentInstructionsDisplayData(
  sections: AdditionalProductConfigSection[],
  getAttachmentInstructionsForAdditionalSections: (sections: AdditionalProductConfigSection[]) => ProductData[],
  getAttachmentInstructionsPrice: (instructions: ProductData[]) => number,
): ProductListAttachmentInstructionsDisplayData | null {
  const attachmentInstructions = getAttachmentInstructionsForAdditionalSections(sections);
  if (attachmentInstructions.length === 0) {
    return null;
  }

  return {
    key: "attachmentInstructions",
    label: localizeRuntimeById("productList.attachmentInstructions", "Gebrauchsanweisungen für Zubehör"),
    subtotal: getAttachmentInstructionsPrice(attachmentInstructions),
    attachmentInstructions,
  };
}

export function isAttachmentInstructionsDisplayData(
  section:
    | ProductListGroupDisplayData<
        FgdsSectionDisplayData,
        ProductListAttachmentInstructionsDisplayData,
        GroupSection
      >["sections"][number]
    | ProductListAttachmentInstructionsDisplayData,
): section is ProductListAttachmentInstructionsDisplayData {
  return "attachmentInstructions" in section;
}

export function getProductSectionHeadline(type: string, isPlaceholder?: boolean) {
  switch (type) {
    case FgdsProductType.ALARMDEVICE: {
      return isPlaceholder
        ? $localize`:@@productListExport.alarmDevicePlaceholders.header:Platzhalter (Alarmmittel)`
        : $localize`:@@global.alarmDevices:Alarmmittel`;
    }
    case FgdsProductType.GASWARNINGCENTER: {
      return isPlaceholder
        ? $localize`:@@productListExport.gasWarningCenterPlaceholders.header:Platzhalter (Gaswarnzentralen)`
        : $localize`:@@global.gasWarningCenters:Gaswarnzentralen`;
    }
    case FgdsProductType.PLASTICSIGN: {
      return isPlaceholder
        ? $localize`:@@productListExport.plasticSignPlaceholders.header:Platzhalter (Kunststoffschilder)`
        : $localize`:@@global.plasticSigns:Kunststoffschilder`;
    }
    case FgdsProductType.SIGNALELEMENT: {
      return isPlaceholder
        ? $localize`:@@productListExport.signalElementPlaceholders.header:Platzhalter (Leuchttransparente)`
        : $localize`:@@global.signalElements:Leuchttransparente`;
    }
    case FgdsProductType.TRANSMITTER: {
      return isPlaceholder
        ? $localize`:@@productListExport.transmitterPlaceholders.header:Platzhalter (Transmitter)`
        : $localize`:@@global.transmitters:Transmitter`;
    }
    case FgdsProductType.FLAME_DETECTOR: {
      return isPlaceholder
        ? $localize`:@@productListExport.flameDetectorPlaceholders.header:Platzhalter (Brandmelder)`
        : $localize`:@@global.flameDetectors:Brandmelder`;
    }
    default:
      return "";
  }
}

export function hasRequiredServices(project: FgdsProject): boolean {
  return [project.assembly, project.installation, project.documentation, project.engineering, project.additionalServices].some(
    (service) => service.required,
  );
}
